// 🔧 user_builder.js
// ✅ Purpose: User builder for the LGV Demo Engine
// ✅ Features:
// - Creates demo users in Firestore
// - Uses centralized demo size configuration
// - Uses generated demo users
// - Stores users under /events/{eventId}/users
// - Returns created users for downstream Vote Builder migration
// - Fully block-commented for clarity and future devs

import { collection, doc } from "firebase/firestore";

import { db } from "../../firebase";
import { logger } from "../../utils/logger";

import { getDemoSizeConfig } from "../config/demo_sizes";
import { generateDemoUsers } from "../generators/user_generator";
import { buildDemoUserDoc } from "../templates/user_defaults";
import { commitBatchedWrites } from "../repositories/batched_writes";

export const buildUsers = async ({
  eventResult,
  demoConfig,
  industryTemplate,
  user,
} = {}) => {
  if (!eventResult?.eventId) {
    throw new Error("User Builder requires an eventId.");
  }

  const eventId = eventResult.eventId;

  const sizeConfig = getDemoSizeConfig(
    demoConfig?.size
  );

  // =====================================================
  // Resolve synthetic user population
  // =====================================================
  // Purpose-built Experiences may explicitly declare a fixed
  // evaluator population through fixedUserCount.
  //
  // Ordinary Experiences continue to honor the selected Demo
  // Size. Manifest defaults are descriptive defaults and are
  // not treated as forced runtime sizing.
  // =====================================================

  const fixedUserCount =
    Number(
      industryTemplate
        ?.fixedUserCount ||
      0
    );

  const resolvedUserCount =
    Number.isInteger(
      fixedUserCount
    ) &&
    fixedUserCount > 0
      ? fixedUserCount
      : sizeConfig.userCount;

  const generatedUsers = generateDemoUsers({
    count:
      resolvedUserCount,

    roleProfile:
      industryTemplate?.scoring
        ?.judgeCount
        ? {
            judgeCount:
              Number(
                industryTemplate.scoring.judgeCount
              ),
          }
        : null,

    industryTemplate,
  });

  logger.log("🧱 User Builder creating Firestore users:", {
    value: {
      eventId,
      plannedUserCount: generatedUsers.length,

      userCountSource:
        resolvedUserCount ===
        fixedUserCount
          ? "fixed_experience"
          : "demo_size",
    },
  });

  const createdUsers = [];

  const operations = [];

  if (user?.uid) {
    const creatorUser = {
      id: user.uid,
      uid: user.uid,
      firstName: user.displayName?.split(/\s+/)?.[0] || "Demo",
      lastName: user.displayName?.split(/\s+/)?.slice(1).join(" ") || "Creator",
      displayName: user.displayName || user.email || "Demo Creator",
      email: user.email || "",
      role: "admin",
      participantType: industryTemplate?.voterLabel || "Voter",
      demoIndustry: industryTemplate?.key || demoConfig?.industry || "other",
      demoSeed: true,
      demoCreator: true,
      active: true,
      displayOrder: resolvedUserCount + 1,
    };

    const creatorRef = doc(db, "events", eventId, "users", user.uid);

    operations.push((batch) =>
      batch.set(creatorRef, creatorUser, { merge: true })
    );

    createdUsers.push({
      ...creatorUser,
      ref: creatorRef,
    });
  }

  for (let index = 0; index < generatedUsers.length; index += 1) {
    const generatedUser = generatedUsers[index];

    const userDoc = buildDemoUserDoc({
      generatedUser,
      displayOrder: index + 1,
    });

    const userRef = doc(collection(db, "events", eventId, "users"));

    operations.push((batch) => batch.set(userRef, userDoc));

    createdUsers.push({
      id: userRef.id,
      ref: userRef,

      ...userDoc,
    });
  }

  await commitBatchedWrites({ operations });

  logger.log("✅ User Builder completed:", {
    value: {
      eventId,
      createdUserCount: createdUsers.length,
    },
  });

  return {
    builder: "user_builder",

    ready: true,

    createdCount: createdUsers.length,

    createdUsers,

    message:
      "User Builder created demo event users.",
  };
};

export const buildUserShell = async ({
  eventResult,
  demoConfig,
  industryTemplate,
  user,
} = {}) => {
  if (!eventResult?.eventId) {
    const sizeConfig = getDemoSizeConfig(
      demoConfig?.size
    );

    return {
      builder: "user_builder",

      ready: false,

      plannedCount:
        Number.isInteger(
          Number(
            industryTemplate
              ?.fixedUserCount ||
            0
          )
        ) &&
        Number(
          industryTemplate
            ?.fixedUserCount ||
          0
        ) > 0
          ? Number(
              industryTemplate
                ?.fixedUserCount
            )
          : sizeConfig.userCount,

      message:
        "User Builder requires an eventId before Firestore creation.",
    };
  }

  return buildUsers({
    eventResult,
    demoConfig,
    industryTemplate,
    user,
  });
};
